import React, { useEffect, useState } from 'react';
import { Loader2, Trash2, Upload } from 'lucide-react';
import { useData } from '../../App';
import { Card } from '../UI';
import { CompanyProfile, SignatureLayout } from '../../types';
import { api } from '../../services/api';
import { errorText } from './shared';
import { confirmDialog } from '../Dialogs';

// Факсимиле подписи и печать ИП: ставятся в PDF приложения, акта и счёта по кнопке «с подписью»
const KINDS = [
    { kind: 'signature' as const, field: 'signatureFile' as const, label: 'Подпись', hint: 'распишитесь на белом листе и сфотографируйте сверху, без тени' },
    { kind: 'stamp' as const, field: 'stampFile' as const, label: 'Печать', hint: 'если есть: оттиск на белом листе (у ИП печать не обязательна)' },
];

export const FacsimileCard: React.FC<{ org: CompanyProfile }> = ({ org }) => {
    const { showNotification, refreshData } = useData();
    const [busy, setBusy] = useState<string | null>(null);
    const [ver, setVer] = useState(Date.now());
    const upload = async (kind: 'signature' | 'stamp', f?: File) => {
        if (!f) return;
        setBusy(kind);
        try {
            await api.uploadFacsimile(org.id, kind, f);
            setVer(Date.now()); await refreshData();
            showNotification('Загружено — фон убран автоматически, проверьте картинку', 'success');
        } catch (e) { showNotification(errorText(e), 'error'); }
        finally { setBusy(null); }
    };
    const remove = async (kind: 'signature' | 'stamp') => {
        if (!(await confirmDialog('Удалить картинку?'))) return;
        await api.deleteFacsimile(org.id, kind).catch(e => showNotification(errorText(e), 'error'));
        refreshData();
    };
    return (
        <Card title="Подпись и печать">
            <p className="text-xs text-slate-500 mb-3">Для PDF «с подписью»: приложение, акт и счёт уходят заказчику уже подписанными с вашей стороны.
                Ставится только по кнопке «PDF с подписью».</p>
            <div className="space-y-3">
                {KINDS.map(k => {
                    const path = org[k.field];
                    return (
                        <div key={k.kind} className="border border-slate-200 rounded-md p-2">
                            <div className="flex items-center gap-2 text-sm">
                                <span className="font-medium text-slate-700">{k.label}</span>
                                <label className="ml-auto inline-flex items-center gap-1 text-xs text-blue-600 hover:underline cursor-pointer">
                                    {busy === k.kind ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} {path ? 'заменить' : 'загрузить'}
                                    <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={e => { upload(k.kind, e.target.files?.[0]); e.target.value = ''; }} />
                                </label>
                                {path && <button className="text-slate-400 hover:text-red-600" title="Удалить" onClick={() => remove(k.kind)}><Trash2 size={13} /></button>}
                            </div>
                            {path ? <div className="mt-2 rounded bg-[repeating-conic-gradient(#f1f5f9_0%_25%,#fff_0%_50%)] bg-[length:16px_16px] p-2 flex justify-center">
                                        <img src={`${api.fileUrl(path)}?v=${ver}`} alt={k.label} className="max-h-24 max-w-full object-contain" /></div>
                                  : <p className="mt-1 text-xs text-slate-400">{k.hint}</p>}
                        </div>);
                })}
            </div>
            {org.signatureFile && <LayoutEditor org={org} ver={ver} />}
        </Card>
    );
};

// Положение подписи на линии (facsimile.py, ТЗ 21.10.2026): подпись по центру отрезка, линия на 65% её высоты сверху;
// сдвиг и ширина — в мм, предпросмотр в масштабе строки подписи акта (линия 40 мм).
const DEFAULT: SignatureLayout = { dx: 0, dy: 0, width: 32 };
const LIMITS = { dx: [-30, 30], dy: [-15, 15], width: [15, 70] } as const;
const LINE_AT = 0.65, LINE_MM = 40, STAMP_MM = 38, K = 3.2; // K — пикселей на мм в предпросмотре

const LayoutEditor: React.FC<{ org: CompanyProfile; ver: number }> = ({ org, ver }) => {
    const { showNotification, refreshData } = useData();
    const saved = { ...DEFAULT, ...(org.signatureLayout || {}) };
    const [lay, setLay] = useState<SignatureLayout>(saved);
    const [ratio, setRatio] = useState({ sig: 1, stamp: 1 });
    const [busy, setBusy] = useState(false);
    useEffect(() => { setLay({ ...DEFAULT, ...(org.signatureLayout || {}) }); }, [org.signatureLayout]);
    const changed = (Object.keys(DEFAULT) as (keyof SignatureLayout)[]).some(k => lay[k] !== saved[k]);
    const save = async () => {
        setBusy(true);
        try { await api.saveFacsimileLayout(org.id, lay); await refreshData(); showNotification('Положение подписи сохранено', 'success'); }
        catch (e) { showNotification(errorText(e), 'error'); }
        finally { setBusy(false); }
    };
    const sigH = lay.width * ratio.sig;
    const [last, ...rest] = (org.name || '').replace(/^ИП\s+/i, '').split(/\s+/);
    const signer = `${last} ${rest.map(x => x[0] ? `${x[0]}.` : '').join('')}`.trim();
    const left = 22, top = Math.max(26, sigH * LINE_AT - lay.dy + 4), cx = left + LINE_MM / 2 + lay.dx; // мм
    const field = (k: keyof SignatureLayout, label: string) => (
        <label className="flex items-center gap-2 text-xs text-slate-600">
            <span className="w-28 shrink-0">{label}</span>
            <input type="range" min={LIMITS[k][0]} max={LIMITS[k][1]} step={0.5} value={lay[k]} className="flex-1 min-w-0"
                   onChange={e => setLay({ ...lay, [k]: Number(e.target.value) })} />
            <input type="number" min={LIMITS[k][0]} max={LIMITS[k][1]} step={0.5} value={lay[k]}
                   className="w-16 border border-slate-300 rounded px-1 py-0.5 text-right"
                   onChange={e => setLay({ ...lay, [k]: Math.min(LIMITS[k][1], Math.max(LIMITS[k][0], Number(e.target.value) || 0)) })} />
        </label>);
    return (
        <div className="mt-3 border border-slate-200 rounded-md p-2">
            <div className="text-sm font-medium text-slate-700 mb-1">Положение подписи на линии</div>
            <div className="overflow-x-auto">
                <div className="relative bg-white border border-slate-100 rounded" style={{ width: (left + LINE_MM + 32) * K, height: (top + 22) * K }}>
                    {org.stampFile && <img src={`${api.fileUrl(org.stampFile)}?v=${ver}`} alt="Печать" className="absolute pointer-events-none"
                        onLoad={e => { const i = e.currentTarget; setRatio(r => ({ ...r, stamp: i.naturalHeight / i.naturalWidth })); }}
                        style={{ width: STAMP_MM * K, left: (cx - lay.width * 0.55 - STAMP_MM / 2) * K, top: (top - STAMP_MM * ratio.stamp / 2) * K }} />}
                    <div className="absolute border-b border-slate-800" style={{ left: left * K, width: LINE_MM * K, top: top * K }} />
                    <div className="absolute text-[11px] text-slate-800 whitespace-nowrap" style={{ left: (left + LINE_MM + 1.5) * K, top: top * K - 14 }}>/ {signer} /</div>
                    <div className="absolute text-[10px] text-slate-800" style={{ left: left * K, top: (top + 5) * K }}>М.П.</div>
                    <img src={`${api.fileUrl(org.signatureFile!)}?v=${ver}`} alt="Подпись" className="absolute pointer-events-none"
                        onLoad={e => { const i = e.currentTarget; setRatio(r => ({ ...r, sig: i.naturalHeight / i.naturalWidth })); }}
                        style={{ width: lay.width * K, left: (cx - lay.width / 2) * K, top: (top - sigH * LINE_AT + lay.dy) * K }} />
                </div>
            </div>
            <div className="space-y-1 mt-2">
                {field('dx', 'Вправо, мм')}
                {field('dy', 'Вниз, мм')}
                {field('width', 'Ширина подписи, мм')}
            </div>
            <div className="flex gap-2 mt-2">
                <button className="px-3 py-1 text-xs rounded bg-blue-600 text-white disabled:opacity-50" disabled={!changed || busy} onClick={save}>
                    {busy ? 'Сохраняю…' : 'Сохранить'}</button>
                <button className="px-3 py-1 text-xs rounded border border-slate-300 text-slate-600" onClick={() => setLay(DEFAULT)}>По умолчанию</button>
            </div>
            <p className="text-[11px] text-slate-400 mt-1">Так подпись встанет в акт, приложение и счёт «с подписью». Минус — влево или вверх.</p>
        </div>
    );
};
